// Generic store factory for CRUD operations
// Moved from captain module to be used by CRM modules (Companies, Deals, Activities)

export const createStore = ({
  name,
  API,
  getters = {},
  mutations: customMutations = {},
  actions = () => ({}),
}) => {
  const mutationTypes = {
    SET: `set${name}s`,
    SET_META: `set${name}sMeta`,
    SET_UI_FLAG: `set${name}UIFlag`,
    ADD: `add${name}`,
    EDIT: `update${name}`,
    DESTROY: `delete${name}`,
    CLEAR: `clear${name}s`,
  };

  const initialState = {
    records: [],
    meta: {
      count: 0,
      current_page: 1,
      total_pages: 1,
    },
    uiFlags: {
      fetchingList: false,
      fetchingItem: false,
      creating: false,
      updating: false,
      deleting: false,
      fetching: false,
    },
  };

  const defaultGetters = {
    getRecords: s => s.records,
    getMeta: s => s.meta,
    getUIFlags: s => s.uiFlags,
    getById: s => id => s.records.find(r => r.id === Number(id)),
  };

  const mutations = {
    [mutationTypes.SET](s, records) {
      s.records = records;
    },
    [mutationTypes.SET_META](s, meta) {
      s.meta = meta;
    },
    [mutationTypes.SET_UI_FLAG](s, flags) {
      s.uiFlags = { ...s.uiFlags, ...flags };
    },
    [mutationTypes.ADD](s, record) {
      const index = s.records.findIndex(r => r.id === record.id);
      if (index !== -1) {
        s.records.splice(index, 1, record);
      } else {
        s.records.push(record);
      }
    },
    [mutationTypes.EDIT](s, record) {
      const index = s.records.findIndex(r => r.id === record.id);
      if (index !== -1) {
        s.records.splice(index, 1, record);
      }
    },
    [mutationTypes.DESTROY](s, id) {
      const index = s.records.findIndex(r => r.id === id);
      if (index !== -1) {
        s.records.splice(index, 1);
      }
    },
    [mutationTypes.CLEAR](s) {
      s.records = [];
      s.meta = { count: 0, current_page: 1, total_pages: 1 };
    },
  };

  const defaultActions = {
    get: async ({ commit }, params = {}) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await API.get(params);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    show: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingItem: true });
      try {
        const { data } = await API.show(id);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingItem: false });
      }
    },
    create: async ({ commit }, data) => {
      commit(mutationTypes.SET_UI_FLAG, { creating: true });
      try {
        const { data: response } = await API.create(data);
        commit(mutationTypes.ADD, response);
        return response;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { creating: false });
      }
    },
    update: async ({ commit }, { id, ...data }) => {
      commit(mutationTypes.SET_UI_FLAG, { updating: true });
      try {
        const { data: response } = await API.update(id, data);
        commit(mutationTypes.EDIT, response);
        return response;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { updating: false });
      }
    },
    delete: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { deleting: true });
      try {
        await API.delete(id);
        commit(mutationTypes.DESTROY, id);
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { deleting: false });
      }
    },
    clear: ({ commit }) => {
      commit(mutationTypes.CLEAR);
    },
  };

  const customActions = actions(mutationTypes);

  return {
    namespaced: true,
    state: initialState,
    getters: { ...defaultGetters, ...getters },
    mutations: { ...mutations, ...customMutations },
    actions: { ...defaultActions, ...customActions },
  };
};

export default { createStore };
